import { ChangeDetectorRef, Component, ElementRef, Injector, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AngularMaterialModule } from './angular-material.module';
import { HeaderComponent } from './header/header.component';
import { TenantBrandingService } from './tenant/tenant-branding.service';
import { TenantBootstrapService } from './tenant/tenant-bootstrap.service';
import { AuthService } from './auth/auth.service';

type AppShellMode = 'tenant' | 'tenant-loading' | 'tenant-error';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    AngularMaterialModule,
    HeaderComponent
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.less']
})
export class AppComponent implements OnInit, OnDestroy {
  isAdminRoute = false;
  isInviteRoute = false;
  isLeagueRoute = false;
  isScorekeeperRoute = false;
  hideChrome = false;
  private routerSubs: Subscription;
  private tenantBootstrapSub: Subscription;
  private tenantShellInitialized = false;
  private tenantShellReady = false;

  constructor(
    private router: Router,
    private tenantBranding: TenantBrandingService,
    private tenantBootstrap: TenantBootstrapService,
    private injector: Injector,
    private changeDetector: ChangeDetectorRef,
  ) {}

  @ViewChild('errorHeading')
  set errorHeading(heading: ElementRef<HTMLHeadingElement> | undefined) {
    heading?.nativeElement.focus();
  }

  get shellMode(): AppShellMode {
    if (this.tenantBootstrap.currentState.status === 'error') return 'tenant-error';
    if (this.tenantBootstrap.currentState.status !== 'ready' || !this.tenantShellReady) {
      return 'tenant-loading';
    }
    return 'tenant';
  }

  get tenantErrorMessage(): string {
    return 'We could not load this league right now. Please try again.';
  }

  get canRetryTenantBootstrap(): boolean {
    return this.tenantBootstrap.currentState.status === 'error';
  }

  ngOnInit() {
    this.tenantBootstrapSub = this.tenantBootstrap.state$.subscribe((state) => {
      if (state.status === 'ready' && !this.tenantShellInitialized) {
        this.tenantShellInitialized = true;
        this.tenantBranding.init();
        void this.activateTenantShell();
      }
    });
    // Check initial route
    this.checkAdminRoute(this.router.url);

    // Subscribe to route changes
    this.routerSubs = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.checkAdminRoute(event.url);
      });
  }

  private checkAdminRoute(url: string) {
    this.isInviteRoute = url.startsWith('/team-invite');
    this.isLeagueRoute = url.startsWith('/league') || url.startsWith('/team/');
    this.isScorekeeperRoute =
      url.startsWith('/schedule-content/live/scorekeeper') ||
      url.startsWith('/schedule-content/live/statkeeper');
    // The player portal has its own sidebar shell — hide the global header/chrome there.
    this.hideChrome = url.startsWith('/my-dashboard') || url.startsWith('/profile');
    const adminRoutes = [
      '/players',
      '/add-player',
      '/team',
      '/import-players',
      '/edit-player',
      '/teams',
      '/add-team',
      '/edit-team',
      '/import-teams',
      '/division-content',
      '/season-content',
      '/schedule-content',
      '/league',
    ];
    this.isAdminRoute = adminRoutes.some(route => url.startsWith(route));
  }

  ngOnDestroy() {
    if (this.routerSubs) {
      this.routerSubs.unsubscribe();
    }
    this.tenantBootstrapSub?.unsubscribe();
  }

  retryTenantBootstrap(): void {
    void this.tenantBootstrap.retry();
  }

  private async activateTenantShell(): Promise<void> {
    try {
      await this.injector.get(AuthService).initSession();
    } catch (error) {
      console.error('Unable to initialize the tenant session.', error);
    } finally {
      this.router.initialNavigation();
      this.tenantShellReady = true;
      this.changeDetector.markForCheck();
    }
  }
}
